import { useLogicData } from '@/features/_data/useLogicData';
import { useUnitData } from '@/features/_data/useUnitData';
import { useQueryClient } from '@tanstack/react-query';
import { Edge, Node, useEdgesState, useNodesState } from '@xyflow/react';
import { useEffect, useRef, useState } from 'react';
import { deepEqual } from 'src/utils/deepEqual';
import { GeneralSettingsType } from '../SettingsSidebar/types';
import {
  GeneralSettingsDefault,
  defaultBehaviourEdges,
  defaultBehaviourNodes,
} from '../defaultData';

export function useLogicInitialization(unitId: string) {
  const queryClient = useQueryClient();

  // Clear logic data from query client on unmount to avoid stale data
  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['unitId', unitId] });
    };
  }, [unitId, queryClient]);

  // fetch unit data
  const {
    data: unit,
    isLoading: isLoadingUnits,
    refetch: refetchUnit,
    isRefetching: isRefetchingUnit,
  } = useUnitData(unitId);

  // fetch logic data and handle states
  const { data: logicData, isSuccess, isError } = useLogicData(unitId);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [settings, setSettings] = useState<GeneralSettingsType>(
    GeneralSettingsDefault,
  );
  // needed for tracking the selected sensors to limit options
  const [selectedSensors, setSelectedSensors] = useState<{
    buttons: string[];
    buzzers: string[];
    trihows: string[];
  }>({ buttons: [], buzzers: [], trihows: [] });

  // Initialization state for loading spinner
  const [isInitialising, setIsInitialising] = useState(true);

  // Dirty state
  const [isDirty, setIsDirty] = useState(false);
  const initialStateRef = useRef<{
    nodes: Node[];
    edges: Edge[];
    settings: GeneralSettingsType;
    selectedSensors: typeof selectedSensors;
  } | null>(null);

  // Load logic data (success/error handling)
  useEffect(() => {
    setIsInitialising(true);

    if (isSuccess && logicData) {
      // ✅ Success case: use fetched logicData
      const fetchedNodes =
        Array.isArray(logicData.nodes) && logicData.nodes.length > 0
          ? logicData.nodes
          : defaultBehaviourNodes;

      const fetchedEdges = Array.isArray(logicData.edges)
        ? logicData.edges
        : defaultBehaviourEdges;

      const fetchedSelectedSensors = logicData.selectedSensors ?? {
        buttons: [],
        buzzers: [],
        trihows: [],
      };

      setNodes(fetchedNodes);
      setEdges(fetchedEdges);
      setSelectedSensors(fetchedSelectedSensors);
      setSettings({ ...GeneralSettingsDefault, ...logicData.settings });

      // store clean snapshot
      initialStateRef.current = {
        nodes: fetchedNodes,
        edges: fetchedEdges,
        settings: { ...GeneralSettingsDefault, ...logicData.settings },
        selectedSensors: fetchedSelectedSensors,
      };
      setIsDirty(false);
    }

    if (isError) {
      // ❌ Error case: initialize with defaults
      setNodes(defaultBehaviourNodes);
      setEdges(defaultBehaviourEdges);
      setSelectedSensors({ buttons: [], buzzers: [], trihows: [] });
      setSettings(GeneralSettingsDefault);

      initialStateRef.current = {
        nodes: defaultBehaviourNodes,
        edges: defaultBehaviourEdges,
        settings: GeneralSettingsDefault,
        selectedSensors: { buttons: [], buzzers: [], trihows: [] },
      };
      setIsDirty(false);
    }

    setIsInitialising(false);
  }, [isSuccess, isError, logicData, setNodes, setEdges]);

  // Mark dirty if nodes, edges, settings or selected sensors change
  useEffect(() => {
    if (!initialStateRef.current) return;
    const dirty =
      !deepEqual(nodes, initialStateRef.current.nodes) ||
      !deepEqual(edges, initialStateRef.current.edges) ||
      !deepEqual(settings, initialStateRef.current.settings) ||
      !deepEqual(selectedSensors, initialStateRef.current.selectedSensors);

    setIsDirty(dirty);
  }, [nodes, edges, settings, selectedSensors]);

  return {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    settings,
    setSettings,
    selectedSensors,
    setSelectedSensors,
    isInitialising,
    unit,
    isLoadingUnits,
    refetchUnit,
    isRefetchingUnit,
    isSuccess,
    isError,
    isDirty,
    setIsDirty, // allow manual reset after save
    initialStateRef,
  };
}
